import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader } from 'react-feather';
import authHelper from 'Utils/Helper/AuthHelpers';
import { useAuth } from 'Utils/Hooks/useAuth';
import { BrandMark } from '../components/AppShell';
import { PRIMARY_BASE_URL } from '../lib/servers';

/**
 * Sign-in. Same endpoint and storage as before (POST /auth/login, token and
 * user in session storage), with clearer errors and an optional "keep me
 * signed in" that uses local storage instead.
 */
export default function Login() {
  const { setCurrentAdmin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  const emailError = touched && !email.trim() ? 'Enter your email address.' : '';
  const passwordError = touched && !password ? 'Enter your password.' : '';

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    setError('');
    if (!email.trim() || !password) return;
    setBusy(true);
    try {
      const res = await fetch(`${PRIMARY_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || !payload.status) {
        setError(
          payload.status_code === 999 || res.status === 401
            ? "That email and password don't match an account. Check them and try again."
            : payload.message || `Sign-in failed (${res.status}). Try again.`
        );
        setBusy(false);
        return;
      }
      const { access_token: token, user } = payload.data || {};
      authHelper.setToken(token, remember);
      authHelper.setUserInfo(user, remember);
      setCurrentAdmin(user);
    } catch (err) {
      setError("Couldn't reach the server. Check your connection and try again.");
      setBusy(false);
    }
  };

  return (
    <div className="hml-login">
      <section className="hml-login-side" aria-label="About this portal">
        <span className="hml-brand">
          <BrandMark />
          <span>
            <span className="hml-brand-name">HML Aggregator</span>
            <span className="hml-brand-sub">Subscriptions across MTN, Airtel, Glo and 9mobile</span>
          </span>
        </span>
        <h1>Every subscription, renewal and naira in one place.</h1>
        <p>Track revenue by product and telco, look up any subscriber by number, and export what you need for reconciliation.</p>
        <div className="hml-login-tape" aria-hidden="true">
          <div>
            <strong>4</strong>
            <span>telco networks</span>
          </div>
          <div>
            <strong>Daily</strong>
            <span>revenue and renewals</span>
          </div>
          <div>
            <strong>CSV</strong>
            <span>exports for finance</span>
          </div>
        </div>
      </section>

      <main className="hml-login-main">
        <form className="hml-login-form" onSubmit={submit} noValidate>
          <h2>Sign in</h2>
          <p>Use the email and password for your HML account.</p>

          {error && (
            <div className="hml-notice hml-notice--error" role="alert">
              <div>{error}</div>
            </div>
          )}

          <div className="hml-field">
            <label htmlFor="login-email">Email address</label>
            <input
              id="login-email"
              className="hml-input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={!!emailError}
              aria-describedby={emailError ? 'login-email-error' : undefined}
              autoFocus
            />
            {emailError && (
              <span className="hml-field-error" id="login-email-error">
                {emailError}
              </span>
            )}
          </div>

          <div className="hml-field">
            <label htmlFor="login-password">Password</label>
            <div className="hml-password">
              <input
                id="login-password"
                className="hml-input"
                type={show ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={!!passwordError}
                aria-describedby={passwordError ? 'login-password-error' : undefined}
              />
              <button type="button" onClick={() => setShow((s) => !s)} aria-pressed={show}>
                {show ? 'Hide' : 'Show'}
              </button>
            </div>
            {passwordError && (
              <span className="hml-field-error" id="login-password-error">
                {passwordError}
              </span>
            )}
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, fontSize: 14, fontWeight: 500, color: 'var(--hml-ink-2)' }}>
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            Keep me signed in on this device
          </label>

          <button type="submit" className="hml-btn hml-btn--primary" disabled={busy}>
            {busy && <Loader className="hml-spin" aria-hidden="true" />}
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          <p className="hml-login-foot">
            New partner? <Link to="/auth/register">Create an account</Link>
          </p>
        </form>
      </main>
    </div>
  );
}
